import { FC, useCallback } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SortableSection from 'components/SortableSection';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Sorting, { Position, useSorting } from 'components/Sorting';
import TextField from 'components/TextField';
import LegacyEditor from 'components/LegacyEditor';
import Dialog from 'components/Dialog';
import AddWhatToSeeBlockDialog, {
  AddedBlock,
} from 'components/AddWhatToSeeBlockDialog';

import { useDelete } from 'lib';
import { WhatToSeeBlocksFieldSections_blocksConnection$key } from '__generated__/WhatToSeeBlocksFieldSections_blocksConnection.graphql';
import { WhatToSeeBlocksFieldSections_whatToSee$key } from '__generated__/WhatToSeeBlocksFieldSections_whatToSee.graphql';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

type Props = {
  blocksConnection: WhatToSeeBlocksFieldSections_blocksConnection$key;
  whatToSee: WhatToSeeBlocksFieldSections_whatToSee$key;
  swap: (oldIndex: number, newIndex: number) => void;
  push: (block: AddedBlock) => void;
  name: string;
};

export type WhatToSeeBlocksFieldSectionsProps = Omit<
  Props,
  'blocksConnection' | 'whatToSee' | 'swap' | 'push'
>;

const WhatToSeeBlocksFieldSections: FC<Props> = props => {
  const { name, swap, push, ...rest } = props;

  const { __id, edges } = useFragment(
    graphql`
      fragment WhatToSeeBlocksFieldSections_blocksConnection on WhatToSeeBlockConnection {
        __id

        edges {
          node {
            id
            name
            description
          }
        }
      }
    `,
    rest.blocksConnection
  );

  const whatToSee = useFragment(
    graphql`
      fragment WhatToSeeBlocksFieldSections_whatToSee on WhatToSee {
        ...AddWhatToSeeBlockDialog_whatToSee
      }
    `,
    rest.whatToSee
  );

  const [sort, sorting] = useSorting(
    graphql`
      mutation WhatToSeeBlocksFieldSectionsMutation(
        $positions: [EntityWithPositionInput!]!
      ) {
        updateWhatToSeeBlockPositions(positions: $positions)
      }
    `,
    __id
  );

  const [deleteBlock] = useDelete(__id);

  const handleDelete = useCallback(
    (id: string) => () => {
      deleteBlock(id);
    },
    [deleteBlock]
  );

  const handleSort = useCallback(
    (positions: Position[], oldIndex: number, newIndex: number) => {
      sort(positions, oldIndex, newIndex)
        .then(() => {})
        .catch(() => {
          swap(oldIndex, newIndex);
        });

      swap(oldIndex, newIndex);
    },
    [sort, swap]
  );

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row-reverse' }}>
        <Dialog
          fullWidth
          maxWidth="sm"
          activateButton={open => (
            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={open}>
              Pridėti
            </Button>
          )}>
          <AddWhatToSeeBlockDialog
            whatToSee={whatToSee}
            connectionId={__id}
            onAdd={push}
          />
        </Dialog>
      </Box>

      <Box>
        <Sorting edges={edges} onSort={handleSort}>
          {edges.map(({ node }, i) => (
            <SortableSection
              key={node.id}
              id={node.id}
              title={node.name}
              defaultExpanded
              sortDisabled={sorting}
              toolbar={
                <ConfirmableIconButton onConfirm={handleDelete(node.id)}>
                  <DeleteIcon fontSize="small" color="error" />
                </ConfirmableIconButton>
              }>
              <Stack spacing={4}>
                <TextField name={`${name}.${i}.name`} label="Pavadinimas" />
                <LegacyEditor
                  name={`${name}.${i}.description`}
                  label="Aprašymas"
                />
              </Stack>
            </SortableSection>
          ))}
        </Sorting>
      </Box>
    </>
  );
};

export default WhatToSeeBlocksFieldSections;
